/* eslint-disable class-methods-use-this */
import { GraphQLException } from 'exceptions';
import Empty from 'constants/empty';
import stringUtils from 'utils/String';

const ERROR_MESSAGE = '{0} [GraphQL] API call returned error: {1}';
const NO_ERROR_MESSAGE = 'Error message was not provided for the error';
const NO_RESPONSE_MESSAGE = '{0} [GraphQL] API call failed, no response received';

// This middleware exists for the sole purpose of throwing an exception when an error is returned from the API.
// GraphQL backend errors are returned in the `errors` array of the response object with HTTP response code 200.
class ErrorsMiddleware {
    async request(context, next) {
        const result = await next();

        // Handle null/undefined results (e.g., from exhausted retries)
        if (!result) {
            throw new GraphQLException(stringUtils.format(NO_RESPONSE_MESSAGE, performance.now()));
        }

        const { errors = Empty.Array, data = null } = result;
        const { allowPartialResponse = false, preserveErrorExtensions = false } = context.options;

        if (errors.length) {
            const firstError = errors[0];

            // Opt-in callers keep `extensions` on the exception; default behavior is unchanged.
            const throwFlattened = () => {
                const errorMessage = stringUtils.format(ERROR_MESSAGE, performance.now(), firstError.message || NO_ERROR_MESSAGE);
                const exception = new GraphQLException(errorMessage);

                if (preserveErrorExtensions && firstError.extensions) {
                    exception.extensions = firstError.extensions;
                }

                throw exception;
            };

            // When partial responses are allowed, only throw if there's no data at all
            if (allowPartialResponse) {
                const hasNoData = !data || (typeof data === 'object' && Object.keys(data).length === 0);

                // Only throw if there are errors AND no usable data (complete failure)
                if (hasNoData) {
                    throwFlattened();
                }

                // Log partial errors for monitoring but don't throw
                Sephora.logger.warn('[GraphQL] Partial errors detected:', errors);
            } else {
                // Default behavior: any errors should result in throwing an exception
                throwFlattened();
            }
        }

        return result;
    }
}

export default ErrorsMiddleware;
